import {Coordinates} from '@domain/coordinates';
import {MachineEfficiency} from '@domain/machine/machine-efficiency';
import {MachineEfficiencyLevel} from '@domain/machine/machine-efficiency-level.enum';
import {MachineEfficiencyUpdate} from '@domain/machine/machine-information-update/machine-efficiency-update.interface';
import {MachineProductionOrderUpdate} from '@domain/machine/machine-information-update/machine-production-order-update.interface';
import {MachineReportItemUpdate} from '@domain/machine/machine-information-update/machine-report-item-update.interface';
import {MachineSpeedUpdate} from '@domain/machine/machine-information-update/machine-speed-update.interface';
import {MachineStatusUpdate} from '@domain/machine/machine-information-update/machine-status-update.interface';
import {MachineQueue} from '@domain/machine/machine-queue';
import {MachineStatus} from '@domain/machine/machine-status.enum';
import {MachineType} from '@domain/machine/machine-type.enum';
import {OverviewMachineProductionOrder} from '@domain/machine/overview-machine-production-order';
import {StopReason} from '@domain/machine/stop-reason.interface';
import {ReportItemTemplateField} from '@domain/report-data-set/report-item-template-field.enum';
import {OverviewListMachineQuality} from '@presentation/pages/textile-data/machine-quality/overview/overview-list-machine-quality';
import {AssertionUtils, Message, Pattern, StringUtils} from '@vdw/angular-component-library';
import {isEqual, isNil} from 'lodash-es';
import {BaseOverviewMachineGroup} from './base-overview-machine-group';
import {OverviewMachineCreel} from './overview-machine-creel';
import {ReportItem} from './report-item';
import {HighestSeverityNotificationContextService} from '@presentation/pages/machine-overview/highest-severity-notification-context/highest-severity-notification-context.service';

export class OverviewMachine {
  private _id: number;
  private _name: string;
  private _machineType: MachineType;
  private _coordinatesInPx: Coordinates;
  private _status: MachineStatus;
  private _queue: MachineQueue;
  private _efficiency: MachineEfficiency;
  private _activeQuality: OverviewListMachineQuality;
  private _activeCreel: OverviewMachineCreel;
  private _messages: Message[];
  private _productionOrder: OverviewMachineProductionOrder;
  private _orderId: string;
  private _rotationsPerMinute: number;
  private _amountOfWarningMessages: number;
  private _roleCode: string;
  private _stopReason: StopReason;
  private _machineGroups: BaseOverviewMachineGroup[];
  private _reportItems: ReportItem[];
  private _remoteMachine: boolean;
  private _endTime: Date;
  private _speed: number;

  public cachedMachineIcon: string;

  public constructor(
    id: number = null,
    name: string = null,
    efficiency: MachineEfficiency = null,
    coordinatesInPx: Coordinates = null,
    machineType: MachineType = null,
    status: MachineStatus = null,
    queue: MachineQueue = null,
    activeQuality: OverviewListMachineQuality = null,
    activeCreel: OverviewMachineCreel = null,
    messages: Message[] = [],
    productionOrder: OverviewMachineProductionOrder = null,
    orderId: string = null,
    rotationsPerMinute: number = null,
    amountOfWarningMessages: number = null,
    roleCode: string = null,
    stopReason: StopReason = null,
    machineGroups: BaseOverviewMachineGroup[] = [],
    reportItems: ReportItem[] = [],
    remoteMachine: boolean = false,
    endTime: Date = null,
    speed: number = null
  ) {
    this._id = id;
    this._name = name;
    this._coordinatesInPx = coordinatesInPx;
    this._machineType = machineType;
    this._status = status;
    this._queue = queue;
    this._efficiency = efficiency;
    this._activeQuality = activeQuality;
    this._activeCreel = activeCreel;
    this._messages = messages;
    this._productionOrder = productionOrder;
    this._orderId = orderId;
    this._rotationsPerMinute = rotationsPerMinute;
    this._amountOfWarningMessages = amountOfWarningMessages;
    this._roleCode = roleCode;
    this._stopReason = stopReason;
    this._machineGroups = machineGroups;
    this._reportItems = reportItems;
    this._remoteMachine = remoteMachine;
    this._endTime = endTime;
    this._speed = speed;
  }

  public static fromJSON(machineJSON: any): OverviewMachine {
    const activeQuality: OverviewListMachineQuality = isNil(machineJSON.activeMachineQuality) ? null : OverviewListMachineQuality.fromJSON(machineJSON.activeMachineQuality);

    const activeCreel: OverviewMachineCreel = isNil(machineJSON.activeCreel) ? null : OverviewMachineCreel.fromJSON(machineJSON.activeCreel);

    return new OverviewMachine(
      machineJSON.id,
      machineJSON.name,
      isNil(machineJSON.efficiency) ? machineJSON.efficiency : {value: machineJSON.efficiency.value, level: machineJSON.efficiency.level},
      machineJSON.coordinatesInPx,
      MachineType[`${machineJSON.machineType}`],
      isNil(machineJSON.status) ? machineJSON.status : MachineStatus[machineJSON.status],
      null,
      activeQuality,
      activeCreel,
      [],
      isNil(machineJSON.productionOrder)
        ? machineJSON.productionOrder
        : new OverviewMachineProductionOrder(machineJSON.productionOrder.name, {
            currentPick: machineJSON.productionOrder.progress.currentPick,
            totalNumberOfPicks: machineJSON.productionOrder.progress.totalNumberOfPicks,
            timeSpentInSeconds: machineJSON.productionOrder.progress.timeSpentInSeconds,
            durationInSeconds: machineJSON.productionOrder.progress.durationInSeconds
          }),
      machineJSON.orderId,
      machineJSON.rotationsPerMinute,
      machineJSON.amountOfWarningMessages,
      machineJSON.roleCode,
      isNil(machineJSON.currentStopReason)
        ? machineJSON.currentStopReason
        : {abbreviation: machineJSON.currentStopReason.abbreviation, pattern: Pattern[machineJSON.currentStopReason.pattern], color: machineJSON.currentStopReason.color},
      machineJSON.machineGroups.map((machineGroup: any) => BaseOverviewMachineGroup.fromJSON(machineGroup)),
      machineJSON.reportItems?.map((reportItem: any) => ReportItem.fromJSON(reportItem)),
      machineJSON.remoteMachine,
      StringUtils.toDate(machineJSON.endTime),
      machineJSON.speed
    );
  }

  public get id(): number {
    return this._id;
  }

  public set id(id: number) {
    this._id = id;
  }

  public get name(): string {
    return this._name;
  }

  public set name(name: string) {
    this._name = name;
  }

  public get coordinatesInPx(): Coordinates {
    return this._coordinatesInPx;
  }

  public set coordinatesInPx(coordinatesInPx: Coordinates) {
    this._coordinatesInPx = coordinatesInPx;
  }

  public get machineType(): MachineType {
    return this._machineType;
  }

  public set machineType(machineType: MachineType) {
    this._machineType = machineType;
  }

  public get status(): MachineStatus {
    return this._status;
  }

  public set status(status: MachineStatus) {
    this._status = status;
  }

  public get queue(): MachineQueue {
    return this._queue;
  }

  public set queue(queue: MachineQueue) {
    this._queue = queue;
  }

  public get efficiency(): MachineEfficiency {
    if (!AssertionUtils.isNullOrUndefined(this._efficiency)) {
      this._efficiency.value = this._efficiency.value > 100 ? 100 : Math.round(this._efficiency.value * 100) / 100;
    }
    return this._efficiency;
  }

  public set efficiency(efficiency: MachineEfficiency) {
    this._efficiency = efficiency;
  }

  public get activeQuality(): OverviewListMachineQuality {
    return this._activeQuality;
  }

  public set activeQuality(activeQuality: OverviewListMachineQuality) {
    this._activeQuality = activeQuality;
  }

  public get activeCreel(): OverviewMachineCreel {
    return this._activeCreel;
  }

  public set activeCreel(activeCreel: OverviewMachineCreel) {
    this._activeCreel = activeCreel;
  }

  public get messages(): Message[] {
    return this._messages;
  }

  public set messages(messages: Message[]) {
    this._messages = messages;
  }

  public get productionOrder(): OverviewMachineProductionOrder {
    return this._productionOrder;
  }

  public set productionOrder(productionOrder: OverviewMachineProductionOrder) {
    this._productionOrder = productionOrder;
  }

  public get orderId(): string {
    return this._orderId;
  }

  public set orderId(orderId: string) {
    this._orderId = orderId;
  }

  public get rotationsPerMinute(): number {
    return this._rotationsPerMinute;
  }

  public set rotationsPerMinute(rotationsPerMinute: number) {
    this._rotationsPerMinute = rotationsPerMinute;
  }

  public get amountOfWarningMessages(): number {
    return this._amountOfWarningMessages;
  }

  public set amountOfWarningMessages(amountOfWarningMessages: number) {
    this._amountOfWarningMessages = amountOfWarningMessages;
  }

  public get roleCode(): string {
    return this._roleCode;
  }

  public set roleCode(roleCode: string) {
    this._roleCode = roleCode;
  }

  public get stopReason(): StopReason {
    return this._stopReason;
  }

  public set stopReason(stopReason: StopReason) {
    this._stopReason = stopReason;
  }

  public get machineGroups(): BaseOverviewMachineGroup[] {
    return this._machineGroups;
  }

  public set machineGroups(machineGroups: BaseOverviewMachineGroup[]) {
    this._machineGroups = machineGroups;
  }

  public get reportItems(): ReportItem[] {
    return this._reportItems;
  }

  public set reportItems(reportItems: ReportItem[]) {
    this._reportItems = reportItems;
  }

  public get remoteMachine(): boolean {
    return this._remoteMachine;
  }

  public set remoteMachine(remoteMachine: boolean) {
    this._remoteMachine = remoteMachine;
  }

  public get endTime(): Date {
    return this._endTime;
  }

  public get speed(): number {
    return this._speed;
  }

  public get amountOfJobsOnQueue(): number {
    return !isNil(this.queue) ? this.queue.amount : null;
  }

  public get queueRemainingTimeInMs(): number {
    return typeof this.queue !== 'undefined' && this.queue !== null ? this.queue.duration : null;
  }

  public get isStopped(): boolean {
    return isEqual(this.status, MachineStatus.STOPPED_MANUAL) || isEqual(this.status, MachineStatus.STOPPED_CRITICAL);
  }

  public get isDisconnected(): boolean {
    return this.status === MachineStatus.DISCONNECTED;
  }

  public isPremium(): boolean {
    return isEqual(this._roleCode, 'M-P');
  }

  public getReportItemValue(field: ReportItemTemplateField): any {
    return this.reportItems?.find((reportItem: ReportItem) => reportItem.field === field)?.value;
  }

  public updateMachineReportItems(data: MachineReportItemUpdate): void {
    const updatedReportItem = new ReportItem(ReportItemTemplateField[data.field], data.value);

    const reportItemExists = this.reportItems?.some((reportItem: ReportItem) => reportItem.field === updatedReportItem.field);

    if (reportItemExists) {
      this.reportItems = this.reportItems.map((reportItem: ReportItem) => (reportItem.field === updatedReportItem.field ? updatedReportItem : reportItem));
    } else {
      this.reportItems.push(updatedReportItem);
    }
  }

  public updateMachineEfficiency(data: MachineEfficiencyUpdate): void {
    this.efficiency = {
      value: data.currentEfficiency.value,
      level: data.currentEfficiency.level as MachineEfficiencyLevel
    };
  }

  public updateMachineStatus(data: MachineStatusUpdate): void {
    this.status = MachineStatus.getMachineStatusByLabel(data.currentStatus);
    this.stopReason = isNil(data.stopReason) ? data.stopReason : {abbreviation: data.stopReason.abbreviation, color: data.stopReason.color, pattern: Pattern[data.stopReason.pattern as any as string]};
  }

  public updateMachineMessage(message: Message): void {
    this.messages.unshift(message);
  }

  public updateMachineProductionOrder(data: MachineProductionOrderUpdate): void {
    this.productionOrder = new OverviewMachineProductionOrder(data.order.name, data.orderProgressChange);
  }

  public updateMachineSpeed(data: MachineSpeedUpdate): void {
    this.rotationsPerMinute = data.rotationsPerMinute;
  }

  public calculateCachedMachineIcon(highestSeverityContextService: HighestSeverityNotificationContextService): void {
    const data = highestSeverityContextService?.getHighestSeverityDataForMachine(this.id);
    this.cachedMachineIcon = AssertionUtils.isNullOrUndefined(data) ? MachineType.getIconNameForMachineType(this.machineType) : `${data.highestPriorityLevel.toLowerCase()}-circle`;
  }
}
